/**
 * ProductVariantsMatrixEditor — merchant-side editor for multi-axis
 * variant products (Color × Size / any two-axis or single-axis).
 *
 * Data contract (matches what the customer axis picker consumes via
 * ``lib/variantAxes.js#hasMatrix``): each variant is one SKU that
 * carries ``option_values: {Color: "Red", Size: "S"}`` alongside its
 * own ``mrp``, ``selling_price``, ``menu_price``, ``stock``, etc.
 *
 * Two sections:
 *  1. **Options** — merchant configures the axes (name + list of
 *     values). e.g. `Color: Red, Black` + `Size: S, M, L`.
 *  2. **Combinations** — auto-generated Cartesian product of the axis
 *     values. Merchant fills price / MRP / stock / default per SKU.
 *     Rows persist their fields as options change (keyed by
 *     ``option_values`` signature) so partial edits aren't lost.
 *
 * The customer-side picker then renders one row per axis, gates
 * invalid combinations, and auto-repairs cross-axis selections
 * (iter116 + iter117).
 */
import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, X } from "lucide-react";

const PRESET_AXES = [
    { name: "Color", suggestions: ["Red", "Black", "Blue", "White", "Green"] },
    { name: "Size", suggestions: ["S", "M", "L", "XL", "XXL"] },
    { name: "Material", suggestions: ["Cotton", "Polyester", "Silk", "Wool"] },
];

const sigOf = (ov) => Object.entries(ov || {})
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}=${v}`).join("|");

/** Derive axes from the existing variant list (so re-opening a saved
 *  product shows the merchant's axes populated correctly). */
function deriveAxes(variants) {
    const map = new Map();
    for (const v of variants || []) {
        for (const [k, val] of Object.entries(v.option_values || {})) {
            if (!map.has(k)) map.set(k, []);
            if (!map.get(k).includes(val)) map.get(k).push(val);
        }
    }
    return [...map.entries()].map(([name, values]) => ({ name, values }));
}

function cartesian(axes) {
    if (axes.length === 0) return [];
    // e.g. [{Color:Red}, {Color:Black}] × [{Size:S}, {Size:M}]
    return axes.reduce((acc, ax) => {
        const out = [];
        for (const partial of acc) {
            for (const val of ax.values) {
                out.push({ ...partial, [ax.name]: val });
            }
        }
        return out;
    }, [{}]);
}

export default function ProductVariantsMatrixEditor({ value, onChange, testId = "matrix" }) {
    const variants = Array.isArray(value) ? value : [];

    // Initialise axes from existing variants (edit mode) or empty (create).
    const [axes, setAxes] = useState(() => deriveAxes(variants));
    const [newAxisName, setNewAxisName] = useState("");

    // Cache of combination overrides keyed by option_values signature so
    // the merchant's price/stock edits survive axis reshapes.
    const [overrides, setOverrides] = useState(() => {
        const m = {};
        for (const v of variants) m[sigOf(v.option_values)] = v;
        return m;
    });

    // Whenever axes or overrides change, recompute the variants list
    // and push it up. Rows are auto-generated from the Cartesian
    // product so the merchant never has to hand-add each SKU.
    const combos = useMemo(() => cartesian(axes.filter((a) => a.values.length > 0)), [axes]);
    useEffect(() => {
        const next = combos.map((ov, idx) => {
            const key = sigOf(ov);
            const prev = overrides[key] || {};
            return {
                id: prev.id || `v_${Math.random().toString(36).slice(2, 9)}`,
                label: Object.values(ov).join(" / "),
                type: axes[0]?.name?.toUpperCase() || "CUSTOM",
                option_values: { ...ov },
                menu_price: Number(prev.menu_price) || 0,
                selling_price: Number(prev.selling_price) || 0,
                mrp: Number(prev.mrp) || 0,
                stock: prev.stock == null ? null : Number(prev.stock),
                is_default: !!prev.is_default,
                enabled: prev.enabled !== false,
                color_hex: prev.color_hex || null,
                sort_order: idx,
            };
        });
        onChange(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [combos, overrides]);

    const addAxis = (name) => {
        const cleaned = (name || "").trim();
        if (!cleaned) return;
        if (axes.some((a) => a.name.toLowerCase() === cleaned.toLowerCase())) return;
        if (axes.length >= 2) return;                                 // cap at 2 axes
        setAxes([...axes, { name: cleaned, values: [] }]);
        setNewAxisName("");
    };
    const removeAxis = (idx) => setAxes(axes.filter((_, i) => i !== idx));
    const addValue = (axisIdx, val) => {
        const cleaned = (val || "").trim();
        if (!cleaned) return;
        const next = [...axes];
        if (next[axisIdx].values.includes(cleaned)) return;
        next[axisIdx] = { ...next[axisIdx], values: [...next[axisIdx].values, cleaned] };
        setAxes(next);
    };
    const removeValue = (axisIdx, val) => {
        const next = [...axes];
        next[axisIdx] = { ...next[axisIdx], values: next[axisIdx].values.filter((x) => x !== val) };
        setAxes(next);
    };
    const patchRow = (sig, patch) => setOverrides({ ...overrides, [sig]: { ...(overrides[sig] || {}), ...patch } });
    const setDefaultRow = (sig) => {
        const next = {};
        for (const combo of combos) {
            const k = sigOf(combo);
            next[k] = { ...(overrides[k] || {}), is_default: k === sig };
        }
        setOverrides(next);
    };

    return (
        <div data-testid={testId} className="space-y-3">
            {/* Options section */}
            <div className="bg-card border border-border rounded-xl p-3">
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">Options</p>
                <p className="text-[11px] text-muted-foreground mb-2">
                    Add up to 2 axes (e.g. <em>Color</em>, <em>Size</em>). Combinations below auto-generate.
                </p>
                <div className="space-y-2">
                    {axes.map((axis, i) => (
                        <AxisEditor
                            key={axis.name}
                            axis={axis}
                            onAddValue={(v) => addValue(i, v)}
                            onRemoveValue={(v) => removeValue(i, v)}
                            onRemove={() => removeAxis(i)}
                            testId={`${testId}-axis-${i}`}
                        />
                    ))}
                </div>
                {axes.length < 2 && (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                        {PRESET_AXES.filter((p) => !axes.some((a) => a.name.toLowerCase() === p.name.toLowerCase())).map((p) => (
                            <button
                                key={p.name}
                                type="button"
                                data-testid={`${testId}-add-axis-${p.name.toLowerCase()}`}
                                onClick={() => addAxis(p.name)}
                                className="bg-primary/10 text-primary border border-primary/40 rounded-lg px-2.5 py-1 text-[11px] font-semibold inline-flex items-center gap-1"
                            >
                                <Plus size={11} /> {p.name}
                            </button>
                        ))}
                        <span className="text-[11px] text-muted-foreground">or</span>
                        <input
                            data-testid={`${testId}-new-axis`}
                            className="inp text-xs w-32"
                            placeholder="Custom axis"
                            value={newAxisName}
                            onChange={(e) => setNewAxisName(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addAxis(newAxisName))}
                        />
                        <button
                            type="button"
                            data-testid={`${testId}-add-custom-axis`}
                            onClick={() => addAxis(newAxisName)}
                            className="bg-card border border-border rounded-lg px-2 py-1 text-[11px]"
                        >Add</button>
                    </div>
                )}
            </div>

            {/* Combinations section */}
            {combos.length > 0 && (
                <div className="bg-card border border-border rounded-xl overflow-hidden">
                    <div className="px-3 py-2 border-b border-border bg-muted/30">
                        <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                            Combinations · {combos.length}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                            Each row is one SKU. Fill in the menu price; selling / MRP auto-derive from tenant commission ladder.
                        </p>
                    </div>
                    <div className="max-h-[400px] overflow-y-auto divide-y divide-border">
                        {combos.map((ov, idx) => {
                            const sig = sigOf(ov);
                            const row = overrides[sig] || {};
                            return (
                                <div
                                    key={sig}
                                    data-testid={`${testId}-row-${idx}`}
                                    className="px-3 py-2 grid grid-cols-[minmax(0,1fr)_auto_auto_auto_auto] gap-2 items-center"
                                >
                                    <div className="min-w-0">
                                        <p className="text-sm font-semibold truncate" data-testid={`${testId}-label-${idx}`}>
                                            {Object.entries(ov).map(([k, v]) => (
                                                <span key={k}>
                                                    <span className="text-muted-foreground text-[10px] uppercase tracking-wider mr-1">{k}</span>
                                                    <span className="mr-2">{v}</span>
                                                </span>
                                            ))}
                                        </p>
                                        {row.enabled === false && <p className="text-[10px] text-destructive">Disabled</p>}
                                    </div>
                                    <input
                                        data-testid={`${testId}-menu-${idx}`}
                                        type="number"
                                        className="inp text-sm w-20"
                                        placeholder="Menu ₹"
                                        value={row.menu_price ?? ""}
                                        onChange={(e) => patchRow(sig, { menu_price: Number(e.target.value) || 0 })}
                                    />
                                    <input
                                        data-testid={`${testId}-stock-${idx}`}
                                        type="number"
                                        className="inp text-sm w-16"
                                        placeholder="Stock"
                                        value={row.stock ?? ""}
                                        onChange={(e) => patchRow(sig, { stock: e.target.value === "" ? null : Number(e.target.value) })}
                                    />
                                    <button
                                        type="button"
                                        data-testid={`${testId}-default-${idx}`}
                                        onClick={() => setDefaultRow(sig)}
                                        title={row.is_default ? "Default variant" : "Make default"}
                                        className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${row.is_default ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground"}`}
                                    >{row.is_default ? "Default" : "Default?"}</button>
                                    <button
                                        type="button"
                                        data-testid={`${testId}-toggle-${idx}`}
                                        onClick={() => patchRow(sig, { enabled: row.enabled === false })}
                                        className="p-1.5 rounded-lg hover:bg-muted"
                                        title={row.enabled === false ? "Enable this SKU" : "Disable this SKU"}
                                    >
                                        {row.enabled === false ? <Plus size={13} /> : <X size={13} />}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

function AxisEditor({ axis, onAddValue, onRemoveValue, onRemove, testId }) {
    const [input, setInput] = useState("");
    const preset = PRESET_AXES.find((p) => p.name.toLowerCase() === axis.name.toLowerCase());
    const suggestions = preset ? preset.suggestions.filter((s) => !axis.values.includes(s)) : [];
    return (
        <div className="bg-muted/30 border border-border rounded-lg p-2" data-testid={testId}>
            <div className="flex items-center gap-2 mb-1.5">
                <span className="text-sm font-semibold">{axis.name}</span>
                <button
                    type="button"
                    data-testid={`${testId}-remove-axis`}
                    onClick={onRemove}
                    className="ml-auto p-1 text-destructive hover:bg-destructive/10 rounded"
                ><Trash2 size={12} /></button>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-1.5">
                {axis.values.map((v) => (
                    <span
                        key={v}
                        data-testid={`${testId}-value-${v}`}
                        className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary border border-primary/40 rounded-full px-2 py-0.5"
                    >
                        {v}
                        <button
                            type="button"
                            data-testid={`${testId}-remove-value-${v}`}
                            onClick={() => onRemoveValue(v)}
                            className="hover:opacity-70"
                        >×</button>
                    </span>
                ))}
            </div>
            <div className="flex gap-1.5">
                <input
                    data-testid={`${testId}-input`}
                    className="inp text-xs flex-1"
                    placeholder={`Add ${axis.name.toLowerCase()} value`}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") { e.preventDefault(); onAddValue(input); setInput(""); }
                    }}
                />
                <button
                    type="button"
                    data-testid={`${testId}-add-value`}
                    onClick={() => { onAddValue(input); setInput(""); }}
                    className="bg-card border border-border rounded-lg px-2"
                ><Plus size={12} /></button>
            </div>
            {suggestions.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                    {suggestions.slice(0, 6).map((s) => (
                        <button
                            key={s}
                            type="button"
                            data-testid={`${testId}-suggest-${s}`}
                            onClick={() => onAddValue(s)}
                            className="text-[10px] bg-card border border-border rounded-full px-2 py-0.5 hover:bg-muted"
                        >+ {s}</button>
                    ))}
                </div>
            )}
        </div>
    );
}
